import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { proxy, releaseProxy, wrap } from 'comlink'

import PythonEditorService from 'services/PythonEditorService'

export default (() => {
  const { getRuntimePackages, getInitializationError } = PythonEditorService,
        INITIAL_STATE = { status: 'initializing', stdout: '', stderr: '', initializationError: undefined }

  const createAttempt = () => {
    const attempt = { initializing: true, cancelled: false }
    attempt.cancelledPromise = new Promise(resolve => {
      attempt.cancel = () => resolve(false)
    })
    return attempt
  }

  const isCurrentAttempt = ({ attempt, attemptRef }) => attemptRef.current === attempt && !attempt.cancelled

  const waitForAttempt = ({ attempt, promise }) => Promise.race([
    Promise.resolve(promise).then(() => true),
    attempt.cancelledPromise
  ])

  const disposeAttempt = ({ attempt }) => {
    if(attempt && !attempt.cancelled) {
      attempt.cancelled = true
      attempt.initializing = false
      attempt.cancel()
      attempt.worker?.removeEventListener('error', attempt.onError)
      attempt.worker?.removeEventListener('messageerror', attempt.onMessageError)
      try {
        attempt.runner?.[releaseProxy]()
      } finally {
        attempt.worker?.terminate()
      }
    }
  }

  const initializeWorker = ({ attempt, packages, onError, onStdout }) => {
    const worker = new Worker(new URL('react-py/dist/workers/python-worker.js', import.meta.url))
    attempt.worker = worker
    attempt.onError = event => {
      event.preventDefault?.()
      onError(event.error || new Error(event.message || 'Python worker failed to start.'))
    }
    attempt.onMessageError = () => onError(new Error('Python worker returned an unreadable message.'))
    worker.addEventListener('error', attempt.onError)
    worker.addEventListener('messageerror', attempt.onMessageError)
    attempt.runner = wrap(worker)
    return attempt.runner.init(proxy(onStdout), proxy(() => {}), 'standard', [packages.official, packages.micropip])
  }

  const initializeAttempt = async ({ attempt, attemptRef, packages, environment, setState }) => {
    const fail = error => {
      if(isCurrentAttempt({ attempt, attemptRef })) {
        setState({ ...INITIAL_STATE, status: 'error', initializationError: getInitializationError({ error, environment, packages }) })
        disposeAttempt({ attempt })
      }
    }

    try {
      const resolved = getRuntimePackages({ packages, environment }),
            initialized = initializeWorker({
              attempt,
              packages: resolved,
              onError: fail,
              onStdout: message => {
                if(isCurrentAttempt({ attempt, attemptRef }) && message !== 'Python initialization complete') {
                  setState(previous => ({ ...previous, stdout: previous.stdout ? `${previous.stdout}\n${message}` : String(message) }))
                }
              }
            }),
            completed = await waitForAttempt({ attempt, promise: initialized })
      if(completed && isCurrentAttempt({ attempt, attemptRef })) {
        attempt.initializing = false
        attempt.running = false
        setState(previous => ({ ...previous, status: 'ready', initializationError: undefined }))
      }
    } catch(error) {
      fail(error)
    }
  }

  const runAttempt = async ({ attempt, attemptRef, code, setState }) => {
    if(!attempt || attempt.cancelled || attempt.initializing || attempt.running) {
      return false
    }
    attempt.running = true
    setState(previous => ({ ...previous, status: 'running', stdout: '', stderr: '' }))
    try {
      const completed = await waitForAttempt({ attempt, promise: attempt.runner.run(code, true) })
      if(completed && isCurrentAttempt({ attempt, attemptRef })) {
        attempt.running = false
        setState(previous => ({ ...previous, status: 'ready' }))
        return true
      }
    } catch(error) {
      if(isCurrentAttempt({ attempt, attemptRef })) {
        attempt.running = false
        setState(previous => ({ ...previous, status: 'ready', stderr: String(error?.message || error) }))
      }
    }
    return false
  }

  const usePythonRuntime = ({ packages, environment }) => {
    const [state, setState] = useState(INITIAL_STATE),
          attemptRef = useRef(),
          mountedRef = useRef(false),
          startInitialization = useCallback(({ keepError = false } = {}) => {
            if(!mountedRef.current) {
              return
            }
            disposeAttempt({ attempt: attemptRef.current })
            const attempt = createAttempt()
            attemptRef.current = attempt
            setState(previous => ({ ...INITIAL_STATE, initializationError: keepError ? previous.initializationError : undefined }))
            initializeAttempt({ attempt, attemptRef, packages, environment, setState })
          }, [environment, packages]),
          restartKernel = useCallback(() => {
            if(!attemptRef.current?.initializing) {
              startInitialization({ keepError: true })
            }
          }, [startInitialization]),
          runPython = useCallback(async ({ code }) => runAttempt({ attempt: attemptRef.current, attemptRef, code, setState }), [])

    useEffect(() => {
      mountedRef.current = true
      startInitialization()
      return () => {
        mountedRef.current = false
        disposeAttempt({ attempt: attemptRef.current })
        attemptRef.current = undefined
      }
    }, [startInitialization])

    return useMemo(() => ({
      stdout: state.stdout,
      stderr: state.stderr,
      initializationError: state.initializationError,
      isLoading: state.status === 'initializing',
      isReady: state.status === 'ready' || state.status === 'running',
      isRunning: state.status === 'running',
      restartKernel,
      interruptExecution: restartKernel,
      runPython
    }), [state, restartKernel, runPython])
  }

  return { usePythonRuntime }
})()
